import React, { createContext, useContext, useState, ReactNode, useEffect, useCallback, useMemo } from 'react';
import { AnimatePresence } from 'framer-motion';
import Toast, { ToastProps } from '../components/ui/Toast';
import { toastService } from '../services/toastService';

interface ToastContextType {
  showToast: (toast: Omit<ToastProps, 'id' | 'onClose'>) => void;
  showSuccess: (title: string, message?: string) => void;
  showError: (title: string, message?: string) => void;
  showWarning: (title: string, message?: string) => void;
  showInfo: (title: string, message?: string) => void;
}

const ToastContext = createContext<ToastContextType | undefined>(undefined);

export const useToast = () => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
};

interface ToastProviderProps {
  children: ReactNode;
}

export const ToastProvider: React.FC<ToastProviderProps> = ({ children }) => {
  const [toasts, setToasts] = useState<(ToastProps & { id: string })[]>([]);

  const removeToast = useCallback((id: string) => {
    setToasts(prev => prev.filter(toast => toast.id !== id));
  }, []);

  const showToast = useCallback((toast: Omit<ToastProps, 'id' | 'onClose'>) => {
    // Create a unique key based on type, title, and message for deduplication
    const toastKey = `${toast.type}-${toast.title}-${toast.message || ''}`;

    setToasts(prev => {
      // Check if a toast with the same content already exists
      const existingToast = prev.find(existingToast => {
        const existingKey = `${existingToast.type}-${existingToast.title}-${existingToast.message || ''}`;
        return existingKey === toastKey;
      });

      // If duplicate exists, don't add a new one
      if (existingToast) {
        return prev;
      }

      // Create new toast
      const id = Math.random().toString(36).substring(2, 11);
      const newToast = {
        ...toast,
        id,
        onClose: removeToast
      };

      return [...prev, newToast];
    });
  }, [removeToast]);

  // Initialize the toast service
  useEffect(() => {
    toastService.initialize((toast) => {
      showToast(toast);
    });
  }, [showToast]);

  const showSuccess = useCallback((title: string, message?: string) => {
    showToast({ type: 'success', title, message });
  }, [showToast]);

  const showError = useCallback((title: string, message?: string) => {
    showToast({ type: 'error', title, message, duration: 7000 }); // Longer duration for errors
  }, [showToast]);

  const showWarning = useCallback((title: string, message?: string) => {
    showToast({ type: 'warning', title, message });
  }, [showToast]);

  const showInfo = useCallback((title: string, message?: string) => {
    showToast({ type: 'info', title, message });
  }, [showToast]);

  const contextValue: ToastContextType = useMemo(() => ({
    showToast,
    showSuccess,
    showError,
    showWarning,
    showInfo
  }), [showToast, showSuccess, showError, showWarning, showInfo]);

  return (
    <ToastContext.Provider value={contextValue}>
      {children}
      
      {/* Toast Container */}
      <div className="fixed top-4 right-4 z-[10000] space-y-2 pointer-events-none w-96">
        <AnimatePresence>
          {toasts.map(toast => (
            <Toast
              key={toast.id}
              {...toast}
            />
          ))}
        </AnimatePresence>
      </div>
    </ToastContext.Provider>
  );
};